import React, { useContext, useMemo } from 'react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Cell,
  ReferenceLine,
  ResponsiveContainer,
} from 'recharts';
import { ThemeContext } from 'styled-components';
import * as s from './styled-chart-histogram';

// Sturges' rule: k = ceil(log2(n)) + 1. Padrao classico simples,
// funciona bem pra distribuicoes razoavelmente normais. Para series
// muito grandes (n > 10k) capamos em 60 bins pra leitura visual nao
// virar floresta de barras finas.
const sturgesBins = (n) =>
  Math.max(8, Math.min(60, Math.ceil(Math.log2(Math.max(n, 2))) + 1));

// Helper: classifica valor numa zona se ranges sao fornecidos. Igual
// ao classifyValue do calcChartStats mas inline pra evitar dependencia
// circular.
const classifyValue = (value, sortedRanges, defaultColor) => {
  if (!sortedRanges || !sortedRanges.length) return defaultColor;
  for (let i = 0; i < sortedRanges.length; i += 1) {
    if (value <= sortedRanges[i].value) return sortedRanges[i].color;
  }
  return sortedRanges[sortedRanges.length - 1].color;
};

const formatNum = (v) => {
  if (!Number.isFinite(v)) return '—';
  if (Math.abs(v) < 100) return v.toFixed(2);
  if (Math.abs(v) < 1000) return v.toFixed(1);
  return Math.round(v).toString();
};

// Histograma da distribuicao de valores de uma serie temporal — view
// alternativa ao time series. Mostra quanto tempo a variavel passou em
// cada faixa de valor. Detecta regime bimodal, skew, picos, etc.
const ChartHistogram = ({ data, ranges, unit, variableName, statsMean, statsMedian }) => {
  const { colors } = useContext(ThemeContext);

  const bins = useMemo(() => {
    if (!data || data.length === 0) return null;
    const values = data
      .map((p) => Number(p.value))
      .filter((v) => Number.isFinite(v));
    if (!values.length) return null;

    const min = Math.min(...values);
    const max = Math.max(...values);
    if (min === max) {
      // Variavel constante — 1 unico bin
      return {
        binWidth: 1,
        bins: [
          {
            binStart: min,
            binEnd: max,
            binCenter: min,
            count: values.length,
            pct: 100,
          },
        ],
        totalCount: values.length,
      };
    }

    const k = sturgesBins(values.length);
    const binWidth = (max - min) / k;
    const binsArr = Array.from({ length: k }, (_, i) => ({
      binStart: min + i * binWidth,
      binEnd: min + (i + 1) * binWidth,
      binCenter: min + (i + 0.5) * binWidth,
      count: 0,
    }));
    values.forEach((v) => {
      let idx = Math.floor((v - min) / binWidth);
      if (idx >= k) idx = k - 1; // garante que o max cai no ultimo bin
      if (idx < 0) idx = 0;
      binsArr[idx].count += 1;
    });
    binsArr.forEach((b) => {
      // eslint-disable-next-line no-param-reassign
      b.pct = (b.count / values.length) * 100;
      // Label compacto pra eixo X: ponto medio do bin formatado
      // eslint-disable-next-line no-param-reassign
      b.label = formatNum(b.binCenter);
    });
    return { binWidth, bins: binsArr, totalCount: values.length };
  }, [data]);

  const sortedRanges = useMemo(
    () => (ranges ? [...ranges].sort((a, b) => a.value - b.value) : null),
    [ranges]
  );

  if (!bins) {
    return (
      <s.Container>
        <s.Header>
          <s.Title>
            Distribuição
            {variableName && <s.SubTitle>— {variableName}</s.SubTitle>}
          </s.Title>
        </s.Header>
        <s.EmptyMessage>Aguardando dados...</s.EmptyMessage>
      </s.Container>
    );
  }

  // Pico (bin com maior count) — destaca contextualmente
  const peakCount = Math.max(...bins.bins.map((b) => b.count));

  return (
    <s.Container>
      <s.Header>
        <s.Title>
          Distribuição
          {variableName && <s.SubTitle>— {variableName}</s.SubTitle>}
        </s.Title>
        <s.InfoLine>
          <s.InfoItem>
            Bins: <strong>{bins.bins.length}</strong>
          </s.InfoItem>
          <s.InfoItem>
            Largura:{' '}
            <strong>
              {formatNum(bins.binWidth)}
              {unit ? ` ${unit}` : ''}
            </strong>
          </s.InfoItem>
          <s.InfoItem>
            Amostras:{' '}
            <strong>{bins.totalCount.toLocaleString('pt-BR')}</strong>
          </s.InfoItem>
        </s.InfoLine>
      </s.Header>
      <s.ChartWrapper>
        <ResponsiveContainer width="100%" height="100%">
          <BarChart
            data={bins.bins}
            margin={{ top: 8, right: 16, bottom: 8, left: 0 }}
            barCategoryGap={1}
          >
            <CartesianGrid
              stroke={colors.disabled}
              strokeDasharray="3 3"
              opacity={0.4}
              vertical={false}
            />
            <XAxis
              dataKey="label"
              tick={{ fill: colors.label, fontSize: 10 }}
              tickLine={false}
              axisLine={{ stroke: colors.disabled, strokeOpacity: 0.5 }}
              interval="preserveStartEnd"
              tickFormatter={(v) => `${v}${unit ? ` ${unit}` : ''}`}
            />
            <YAxis
              tick={{ fill: colors.label, fontSize: 10 }}
              tickLine={false}
              axisLine={false}
              width={42}
              label={{
                value: 'amostras',
                angle: -90,
                position: 'insideLeft',
                fill: colors.label,
                fontSize: 10,
                offset: 10,
              }}
            />
            <Tooltip
              cursor={{ fill: colors.disabled, fillOpacity: 0.15 }}
              contentStyle={{
                background: colors.card,
                border: `1px solid ${colors.disabled}`,
                borderRadius: 8,
                fontSize: 12,
              }}
              labelFormatter={(label, payload) => {
                if (!payload || !payload[0]) return label;
                const p = payload[0].payload;
                return `${formatNum(p.binStart)} – ${formatNum(p.binEnd)} ${unit || ''}`;
              }}
              formatter={(value, _name, props) => {
                const { pct } = props.payload;
                return [
                  `${value.toLocaleString('pt-BR')} (${pct.toFixed(1)}%)`,
                  'Amostras',
                ];
              }}
            />
            {/* Linhas de referencia: media + mediana sobre o histograma —
                conecta visualmente com o painel de stats. */}
            {Number.isFinite(statsMean) && (
              <ReferenceLine
                x={formatNum(statsMean)}
                stroke={colors.label}
                strokeDasharray="3 6"
                strokeOpacity={0.5}
                label={{
                  value: 'méd',
                  position: 'top',
                  fill: colors.label,
                  fontSize: 9,
                  fontWeight: 600,
                }}
              />
            )}
            {Number.isFinite(statsMedian) && (
              <ReferenceLine
                x={formatNum(statsMedian)}
                stroke={colors.darkblue}
                strokeDasharray="2 4"
                strokeOpacity={0.4}
                label={{
                  value: 'mediana',
                  position: 'insideTopRight',
                  fill: colors.darkblue,
                  fontSize: 9,
                  fontWeight: 600,
                }}
              />
            )}
            <Bar
              dataKey="count"
              radius={[3, 3, 0, 0]}
              isAnimationActive={false}
            >
              {bins.bins.map((b) => {
                // Cor da barra: zona do range se houver, senao default
                // lightblue. Pico destacado com opacity 1, demais 0.75.
                const isPeak = b.count === peakCount;
                const baseColor = classifyValue(
                  b.binCenter,
                  sortedRanges,
                  colors.lightblue
                );
                return (
                  <Cell
                    key={`${b.binStart}-${b.binEnd}`}
                    fill={baseColor}
                    fillOpacity={isPeak ? 1 : 0.75}
                  />
                );
              })}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </s.ChartWrapper>
    </s.Container>
  );
};

// React.memo: evita re-render no toggle de view dentro do modal quando
// data/ranges nao mudam. Bins sao caros de recomputar — useMemo
// interno depende de data, mas se o componente inteiro re-renderizar
// sem motivo, useMemo dispara mesmo assim.
export default React.memo(ChartHistogram);
